import { useState } from 'react';
import { useFindPricelistsByClientQuery, useLazyGetPricelistByIdQuery } from 'store/api/pricelists';
import { ScopeType, StagedPricelistPeriod } from 'types/pricelists';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { copiedPeriods, isEmployeePriced } from './employeePricing';

interface Props {
    currencyTypeCode?: string | null;
    onPick: (periods: StagedPricelistPeriod[]) => void;
}

/**
 * The firm's employee-priced model lists in the contract's currency; picking one copies its periods and prices
 * into the open editor, which the user then adjusts.
 */
export function EmployeePricelistStartFrom({ currencyTypeCode, onPick }: Props): JSX.Element {
    const { data: pricelists } = useFindPricelistsByClientQuery('model');
    const [fetchPricelist, { isFetching }] = useLazyGetPricelistByIdQuery();
    const [pickedId, setPickedId] = useState<number | null>(null);
    const models = (pricelists ?? []).filter(
        (pricelist) =>
            isEmployeePriced(pricelist) &&
            (pricelist.scopeTypeCode === ScopeType.TENANT || pricelist.scopeTypeCode === ScopeType.COMPANY) &&
            (!currencyTypeCode || pricelist.currencyTypeCode === currencyTypeCode)
    );

    const pick = async (id: number) => {
        setPickedId(id);
        try {
            const detail = await fetchPricelist(id).unwrap();
            onPick(copiedPeriods(detail.periods));
        } catch {
            // The API layer has already said the read failed; the open prices stay as they were.
            setPickedId(null);
        }
    };

    return (
        <div className="flex flex-col gap-1.5">
            <div className="text-grey-500 text-[0.72rem]">{t('pricelists.employee.start-from')}</div>
            {models.length === 0 ? (
                <div className="text-grey-500 text-[0.78rem]">{t('pricelists.employee.no-models')}</div>
            ) : (
                models.map((model) => (
                    <button
                        key={model.id}
                        type="button"
                        aria-pressed={pickedId === model.id}
                        disabled={isFetching}
                        className={cn(
                            'border-grey-300 bg-paper cursor-pointer rounded-[5px] border px-3 py-2 text-left text-[0.8rem]',
                            pickedId === model.id && 'border-primary'
                        )}
                        onClick={() => void pick(model.id)}
                    >
                        <span className="font-medium">{model.name}</span>
                    </button>
                ))
            )}
        </div>
    );
}
